/**
 * WPS field-link registry (slice 08 of wps-pdf-xlsx-faithful).
 *
 * Mirrors `pqrFieldLinkRegistry.ts` for the WPS preview canvas:
 * a single typed source of truth mapping each clickable cell on the
 * canvas to a concrete navigation target on the WPS source page.
 *
 * Unlike PQR, the WPS preview is pipe-set-scoped (NOT weld-scoped),
 * so the context only needs `(projectId, pipeSetId, previewUrl)` —
 * no `weldId` / `syntheticWeldId` plumbing.
 *
 * Imported by `WpsPreviewFieldLink` to build URLs from a click, and
 * (in future slices) by each destination page's
 * `useFieldFocusHandler` registry to interpret the focus params on
 * arrival.
 */

export type WpsFieldDescriptor =
  /**
   * Identifiers (WPS#, Sheet, Rev) on the TopBand title. Modal-routing
   * variant — opens the existing `WpsIdentifiersModal` on the preview
   * page in-place rather than navigating away. Added in slice 04 of
   * `wps-preview-pixel-parity`.
   */
  | { section: 'identifiers' }
  /**
   * Approval signature columns (Company / Engineer / Inspector) on
   * the `ApprovalsFooter`. Modal-routing variant — opens the existing
   * `WpsApprovalsModal` on the preview page in-place. Added in slice
   * 04 of `wps-preview-pixel-parity`.
   */
  | { section: 'approvals' }
  /**
   * Project-info cells on the `TopMetaBlock` (Contractor, Project,
   * CRC Weld File No., Project Mgr, Welding Eng, Date, etc.). Routes
   * to the project edit page with the supplied `focusField` marker
   * applied. Added in slice 04 of `wps-preview-pixel-parity`.
   */
  | { section: 'project-info'; focusField: string }
  /** Base Material rows (XLSX rows 7-13). Whole table → `/base`. */
  | { section: 'base-material' }
  /**
   * Joint/Bevel left half + schematic image (XLSX rows 14-16 + K7:L18).
   * Optional `bevelIndex` flashes a specific bevel tab on arrival.
   */
  | { section: 'joint-bevel'; bevelIndex?: number }
  /** Clamp Information (XLSX rows 17-18). Optional `fieldKey` focus. */
  | { section: 'clamp'; fieldKey?: string }
  /** Preheat / Interpass (XLSX rows 19-25). Optional `fieldKey`. */
  | { section: 'preheat-interpass'; fieldKey?: string }
  /** Weld Times (XLSX rows 29-30). Optional `fieldKey`. */
  | { section: 'weld-times'; fieldKey?: string }
  /** Operator / Welder counts (XLSX rows 27-28). */
  | { section: 'welder-counts' }
  /**
   * Pass Sequence diagram + pass-notes (XLSX K19:S27). Optional
   * `bevelIndex` flashes the bevel tab on arrival (the same diagram
   * is rendered per active bevel).
   */
  | { section: 'pass-sequence'; bevelIndex?: number }
  /**
   * Per-cell on the Weld Details table (rendered when granular
   * welding-variable cells are linked from the preview).
   */
  | {
      section: 'weld-details-cell'
      bevelIndex: number
      passLabel: string
      rowKey: string
    }
  /** Per-cell on the Welding Parameters matrix (XLSX rows 31-58). */
  | {
      section: 'welding-parameter-cell'
      passLabel: string
      columnKey: string
    }
  /** Cleaning / Grinding paragraph (XLSX rows 59-60). */
  | { section: 'cleaning-grinding' }
  /** Post Heating / PWHT / Cooling (XLSX rows 61-63). Optional `fieldKey`. */
  | { section: 'post-weld-heating'; fieldKey?: string }
  /**
   * Notes paragraph(s) at the bottom of the canvas. Routes to
   * `WpsForm` with `focusField=notesTextarea` so the notes textarea
   * scrolls into view, flashes, and receives keyboard focus on
   * arrival. Added in slice 06 of `wps-preview-pixel-parity`.
   *
   * A modal-routing variant was considered and intentionally
   * rejected — the navigate-then-focus path reuses
   * `useFieldFocusHandler` end-to-end and keeps slice 06 small.
   */
  | { section: 'notes' }

export interface WpsFieldLinkContext {
  projectId: string
  pipeSetId: string
  /** Pathname + search of the WPS preview page; round-tripped as `returnTo`. */
  previewUrl: string
  /**
   * Optional in-place modal opener. Supplied by `WpsPreviewPage` to
   * wire modal-routing descriptors (`identifiers`, `approvals`) to
   * the existing modal state. When omitted (e.g. read-only embeds,
   * tests) modal-target clicks fall through to inert behaviour.
   * Added in slice 04 of `wps-preview-pixel-parity`.
   */
  onModalOpen?: (modalKey: 'identifiers' | 'approvals') => void
  /**
   * Optional navigation guard. When supplied, every URL-targeted
   * field click routes through this hook instead of `navigate(...)`
   * directly. The page wires it to `WpsNavigateConfirmModal` so the
   * user gets a "Leave WPS Preview?" prompt (clean or dirty variant)
   * before any in-flight identifier / approvals / notes edits get
   * silently dropped. When omitted, `WpsPreviewFieldLink` falls back
   * to immediate navigation — the historical behaviour. Added in
   * slice C.4 of `wps-product-parity-fixes`.
   */
  requestNavigate?: (request: {
    target: WpsFieldNavTarget
    fieldLabel: string
    proceed: () => void
  }) => void
}

export interface WpsFieldNavTarget {
  kind: 'nav'
  destinationPath: string
  focusParams: Record<string, string>
  returnTo: string
  fullUrl: string
  destinationLabel: string
}

/**
 * Modal-routing target shape — produced by `buildWpsFieldNavTarget`
 * for descriptors whose source lives in an in-place modal on the
 * preview page rather than on a separate route. `WpsPreviewFieldLink`
 * dispatches to `context.onModalOpen(modalKey)` instead of navigating.
 * Added in slice 04 of `wps-preview-pixel-parity`.
 */
export interface WpsFieldModalTarget {
  kind: 'modal'
  modalKey: 'identifiers' | 'approvals'
  destinationLabel: string
}

export type WpsFieldTarget = WpsFieldNavTarget | WpsFieldModalTarget

function buildSearch(focusParams: Record<string, string>, returnTo: string): string {
  const params = new URLSearchParams()
  Object.entries(focusParams).forEach(([k, v]) => params.set(k, v))
  params.set('returnTo', returnTo)
  return params.toString()
}

function basePath(context: WpsFieldLinkContext): string {
  return `/projects/${context.projectId}/next/${context.pipeSetId}`
}

export function buildWpsFieldNavTarget(
  descriptor: WpsFieldDescriptor,
  context: WpsFieldLinkContext
): WpsFieldTarget | null {
  const root = basePath(context)
  switch (descriptor.section) {
    case 'identifiers': {
      return {
        kind: 'modal',
        modalKey: 'identifiers',
        destinationLabel: 'WPS identifiers'
      }
    }
    case 'approvals': {
      return {
        kind: 'modal',
        modalKey: 'approvals',
        destinationLabel: 'Approvals'
      }
    }
    case 'project-info': {
      const destinationPath = `/projects/${context.projectId}/edit`
      const focusParams: Record<string, string> = {
        focusField: descriptor.focusField
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Project details'
      }
    }
    case 'base-material': {
      const destinationPath = `${root}/base`
      const focusParams: Record<string, string> = {}
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Base Material'
      }
    }
    case 'joint-bevel': {
      const destinationPath = `${root}/bevel`
      const focusParams: Record<string, string> = {}
      if (descriptor.bevelIndex !== undefined) {
        focusParams.focusBevel = String(descriptor.bevelIndex)
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Bevel Details'
      }
    }
    case 'clamp': {
      const destinationPath = `${root}/clamp`
      const focusParams: Record<string, string> = {}
      if (descriptor.fieldKey) focusParams.focusField = descriptor.fieldKey
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Clamp Details'
      }
    }
    case 'preheat-interpass': {
      const destinationPath = `${root}/preheat`
      const focusParams: Record<string, string> = {}
      if (descriptor.fieldKey) focusParams.focusField = descriptor.fieldKey
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Preheat Details'
      }
    }
    case 'weld-times': {
      const destinationPath = `${root}/weld-times`
      const focusParams: Record<string, string> = {}
      if (descriptor.fieldKey) focusParams.focusField = descriptor.fieldKey
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Weld Times'
      }
    }
    case 'welder-counts': {
      const destinationPath = `${root}/welder-details`
      const focusParams: Record<string, string> = {}
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Welder Details'
      }
    }
    case 'pass-sequence': {
      const destinationPath = `${root}/pass`
      const focusParams: Record<string, string> = {}
      if (descriptor.bevelIndex !== undefined) {
        focusParams.focusBevel = String(descriptor.bevelIndex)
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Pass Sequence'
      }
    }
    case 'weld-details-cell': {
      // Per-pass route uses encoded pass label to survive labels with
      // spaces (e.g. "Hot Pass" / "Fill 1").
      const destinationPath =
        `${root}/pass/bevel/${descriptor.bevelIndex}/` +
        encodeURIComponent(descriptor.passLabel)
      const focusParams: Record<string, string> = {
        focusField: descriptor.rowKey
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: `Weld Details — ${descriptor.passLabel}`
      }
    }
    case 'welding-parameter-cell': {
      const destinationPath = `${root}/parameters`
      const focusParams: Record<string, string> = {
        focusField: descriptor.columnKey,
        focusPass: descriptor.passLabel
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: `Welding Parameters — ${descriptor.passLabel}`
      }
    }
    case 'cleaning-grinding': {
      const destinationPath = `${root}/post-weld`
      const focusParams: Record<string, string> = {
        focusField: 'cleaningGrindingNotes'
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Post Weld — Cleaning'
      }
    }
    case 'post-weld-heating': {
      const destinationPath = `${root}/post-weld`
      const focusParams: Record<string, string> = {}
      if (descriptor.fieldKey) focusParams.focusField = descriptor.fieldKey
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Post Weld Heating'
      }
    }
    case 'notes': {
      // Routes to WpsForm (the pipe-set root) with a single
      // `focusField=notesTextarea` marker. WpsForm's
      // `useFieldFocusHandler` scrolls + flashes + focuses the
      // shared notes textarea on arrival (slice 06 of
      // `wps-preview-pixel-parity`).
      const destinationPath = root
      const focusParams: Record<string, string> = {
        focusField: 'notesTextarea'
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        kind: 'nav',
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'WPS notes'
      }
    }
  }
}
